// OrganizersPage.jsx
import React, { useEffect, useState, useCallback } from "react";
import CCService from "../../services/httpServiceWithoutSuperAdmin";

/* ----------------------------- Helpers ----------------------------- */
function cls(...args) {
  return args.filter(Boolean).join(" ");
}

function formatMs(epochMs) {
  try {
    return new Date(Number(epochMs)).toLocaleString();
  } catch {
    return "-";
  }
}

/* ----------------------------- Styles (single object) ----------------------------- */
const styles = {
  pageWrap: { padding: 24 },
  container: {
    maxWidth: 1024,
    margin: "0 auto",
    fontFamily:
      "Inter, Roboto, system-ui, -apple-system, 'Segoe UI', 'Helvetica Neue', Arial",
  },

  // header
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  titleWrap: {},
  title: { fontSize: 20, fontWeight: 600, margin: 0 },
  subtitle: { fontSize: 13, color: "#6B7280", marginTop: 6 },

  // search area
  searchRow: { display: "flex", alignItems: "center", gap: 12 },
  searchInput: {
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #E5E7EB",
    outline: "none",
    fontSize: 14,
  },
  newBtn: {
    padding: "8px 12px",
    borderRadius: 8,
    backgroundColor: "#4F46E5",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    fontWeight: 600,
  },

  // table card
  card: {
    background: "#fff",
    borderRadius: 10,
    boxShadow: "0 6px 18px rgba(15,23,42,0.06)",
    overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "collapse", tableLayout: "fixed" },
  thead: { background: "#F1F5F9" },
  th: {
    textAlign: "left",
    padding: "12px 16px",
    fontSize: 13,
    fontWeight: 600,
  },
  td: { padding: "12px 16px", verticalAlign: "top", fontSize: 14 },

  // table states
  emptyRow: { padding: 24, textAlign: "center", color: "#64748B" },
  loadingRow: { padding: 24, textAlign: "center", color: "#64748B" },

  // actions
  actionBtn: {
    padding: "6px 10px",
    borderRadius: 8,
    border: "1px solid rgba(0,0,0,0.06)",
    background: "#fff",
    cursor: "pointer",
    fontSize: 13,
  },
  deleteBtn: {
    padding: "6px 10px",
    borderRadius: 8,
    border: "1px solid rgba(0,0,0,0.06)",
    background: "#fff",
    cursor: "pointer",
    fontSize: 13,
    color: "#DC2626",
  },

  // footer / pagination
  footerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    borderTop: "1px solid #F1F5F9",
    background: "#F8FAFC",
  },
  pageSizeSelect: {
    padding: "6px 8px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    outline: "none",
  },
  pagerBtn: {
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid rgba(0,0,0,0.06)",
    background: "#fff",
    cursor: "pointer",
  },

  // modal shared
  modalBackdrop: {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
  },
  modalWrap: {
    position: "fixed",
    inset: 0,
    zIndex: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  modalCard: {
    position: "relative",
    background: "#fff",
    borderRadius: 10,
    width: "100%",
    maxWidth: 560,
    padding: 20,
    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
    zIndex: 60,
  },

  // modal form
  formGroup: { marginBottom: 12 },
  label: { display: "block", fontSize: 13, marginBottom: 6, fontWeight: 600 },
  input: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #E5E7EB",
    boxSizing: "border-box",
    fontSize: 14,
  },
  errorText: { color: "#DC2626", fontSize: 13, marginTop: 6 },

  // confirm modal
  confirmActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    marginTop: 12,
  },
};

/* ---------------------------- ConfirmModal ------------------------- */
function ConfirmModal({ open, title, description, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div role="dialog" aria-modal="true" style={styles.modalWrap}>
      <div style={styles.modalBackdrop} onClick={onCancel} />
      <div style={styles.modalCard} aria-labelledby="confirm-title">
        <h3
          id="confirm-title"
          style={{ fontSize: 18, fontWeight: 600, margin: 0 }}
        >
          {title}
        </h3>
        <p style={{ color: "#475569", fontSize: 14, marginTop: 8 }}>
          {description}
        </p>

        <div style={styles.confirmActions}>
          <button style={{ ...styles.actionBtn }} onClick={onCancel}>
            Cancel
          </button>
          <button
            style={{
              ...styles.deleteBtn,
              background: "#DC2626",
              color: "#fff",
              border: "none",
            }}
            onClick={onConfirm}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- OrganizerForm ------------------------- */
function OrganizerForm({ open, initial = null, onClose, onSaved }) {
  const isEdit = Boolean(initial && initial.id);
  const [name, setName] = useState(initial?.name ?? "");
  const [company, setCompany] = useState(initial?.company ?? "");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const services = new CCService();

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? "");
      setCompany(initial?.company ?? "");
      setError(null);
      setSubmitting(false);
    }
  }, [open, initial]);

  if (!open) return null;

  const validate = () => {
    if (!name.trim()) return "Name is required.";
    if (name.trim().length > 100) return "Name must be <= 100 characters.";
    if (company && company.length > 150)
      return "Company must be <= 150 characters.";
    return null;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const payload = { name: name.trim(), company: company?.trim() || null };

      if (isEdit) {
        const res = await services.put(
          "/ahead/organizers/" + initial.id,
          payload
        );
        // optionally inspect res
        console.log("res update", res);
      } else {
        const res = await services.post("/ahead/organizers", payload);
        console.log("res create", res);
      }

      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("save organizer err", err);
      setError("Network error.");
      setSubmitting(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" style={styles.modalWrap}>
      <div style={styles.modalBackdrop} onClick={onClose} />
      <form
        onSubmit={handleSubmit}
        style={styles.modalCard}
        aria-label={isEdit ? "Edit organizer" : "New organizer"}
      >
        <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
          {isEdit ? "Edit Organizer" : "New Organizer"}
        </h3>

        <div style={styles.formGroup}>
          <label style={styles.label}>Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={styles.input}
            placeholder="Full name"
            required
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Company</label>
          <input
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            style={styles.input}
            placeholder="Company (optional)"
          />
        </div>

        {error && <div style={styles.errorText}>{error}</div>}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            marginTop: 12,
          }}
        >
          <button
            type="button"
            style={{ ...styles.actionBtn }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            style={{
              ...styles.actionBtn,
              background: submitting ? "#A78BFA" : "#4F46E5",
              color: "#fff",
              border: "none",
              cursor: submitting ? "not-allowed" : "pointer",
            }}
          >
            {submitting ? "Saving..." : isEdit ? "Save changes" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* --------------------------- OrganizersPage ------------------------- */
export default function OrganizersPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editInitial, setEditInitial] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [error, setError] = useState(null);

  const service = new CCService();

  const fetchList = useCallback(
    async (opts = {}) => {
      setLoading(true);
      setError(null);
      try {
        const limit = opts.limit ?? pageSize;
        const off = opts.offset ?? offset;
        const params = new URLSearchParams();
        params.set("limit", limit);
        params.set("offset", off);
        if (q) params.set("q", q);

        const res = await service.get("/ahead/organizers?" + params.toString());
        // console.log("res", res);

        // Accept multiple shapes: res.data (array), res.items, or res directly
        const payload = res?.data ?? res;
        if (Array.isArray(payload)) setItems(payload);
        else if (Array.isArray(payload?.items)) setItems(payload.items);
        else setItems([]);

        const total = res?.total ?? res?.count ?? payload?.total ?? null;
        setTotalCount(total);
      } catch (err) {
        console.error("fetch organizers", err);
        setError("Failed to load organizers");
      } finally {
        setLoading(false);
      }
    },
    [pageSize, offset, q]
  );

  useEffect(() => {
    fetchList({ offset: 0 });
    setOffset(0);
  }, [q, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchList({ offset });
  }, [offset]); // eslint-disable-line react-hooks/exhaustive-deps

  /* create/edit */
  const openCreate = () => {
    setEditInitial(null);
    setModalOpen(true);
  };
  const openEdit = (item) => {
    setEditInitial(item);
    setModalOpen(true);
  };

  /* delete with check */
  const handleDeleteClick = (item) => {
    setToDelete(item);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setConfirmOpen(false);
    setLoading(true);
    setError(null);
    try {
      const res = await service.delete(`/ahead/organizers/${toDelete.id}`);
      console.log("DELETE", res);
      await fetchList({ offset: 0 });
    } catch (err) {
      console.error("delete organizer", err);
      setError("Failed to delete");
    } finally {
      setToDelete(null);
      setLoading(false);
    }
  };

  const handleSaved = () => {
    setModalOpen(false);
    fetchList({ offset: 0 });
  };

  /* simple pagination helpers */
  const canPrev = offset > 0;
  const canNext = items.length === pageSize;
  const gotoPrev = () => setOffset(Math.max(0, offset - pageSize));
  const gotoNext = () => setOffset(offset + pageSize);

  return (
    <div style={styles.pageWrap}>
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.titleWrap}>
            <h1 style={styles.title}>Organizers</h1>
            <p style={styles.subtitle}>
              Manage organizer profiles used by sessions.
            </p>
          </div>

          <div style={styles.searchRow}>
            <input
              type="search"
              placeholder="Search by name or company..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={styles.searchInput}
            />
            <button onClick={openCreate} style={styles.newBtn}>
              + New
            </button>
          </div>
        </div>

        <div style={styles.card}>
          <table style={styles.table} aria-label="Organizers table">
            <thead style={styles.thead}>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Company</th>
                <th style={styles.th}>Created</th>
                <th style={styles.th}>Updated</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan="5" style={styles.loadingRow}>
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan="5" style={styles.emptyRow}>
                    No organizers found.
                  </td>
                </tr>
              ) : (
                items.map((it) => (
                  <tr
                    key={it.id}
                    style={{ borderTop: "1px solid rgba(0,0,0,0.04)" }}
                  >
                    <td style={styles.td}>
                      <div style={{ fontWeight: 600 }}>{it.name}</div>
                    </td>

                    <td style={styles.td}>
                      <div style={{ color: "#64748B", fontSize: 13 }}>
                        {it.company ?? "—"}
                      </div>
                    </td>

                    <td style={styles.td}>
                      <div style={{ color: "#64748B", fontSize: 13 }}>
                        {formatMs(it.created_at)}
                      </div>
                    </td>

                    <td style={styles.td}>
                      <div style={{ color: "#64748B", fontSize: 13 }}>
                        {formatMs(it.updated_at)}
                      </div>
                    </td>

                    <td style={{ ...styles.td }}>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={() => openEdit(it)}
                          style={styles.actionBtn}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(it)}
                          style={styles.deleteBtn}
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div style={styles.footerRow}>
            <div style={{ color: "#64748B", fontSize: 13 }}>
              Showing {items.length} {totalCount ? `of ${totalCount}` : ""}{" "}
              organizers
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setOffset(0);
                }}
                style={styles.pageSizeSelect}
                aria-label="Page size"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
              </select>

              <button
                onClick={gotoPrev}
                disabled={!canPrev}
                style={{
                  ...styles.pagerBtn,
                  opacity: canPrev ? 1 : 0.5,
                  cursor: canPrev ? "pointer" : "not-allowed",
                }}
              >
                Prev
              </button>
              <button
                onClick={gotoNext}
                disabled={!canNext}
                style={{
                  ...styles.pagerBtn,
                  opacity: canNext ? 1 : 0.5,
                  cursor: canNext ? "pointer" : "not-allowed",
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div style={{ marginTop: 12, color: "#DC2626" }}>{error}</div>
        )}
      </div>

      <OrganizerForm
        open={modalOpen}
        initial={editInitial}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Delete organizer?"
        description={`Delete "${toDelete?.name}"? This is permanent and will fail if the organizer is linked to sessions.`}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
